"use client";

import { useMemo, useState } from "react";
import {
  CopyButton,
  Notice,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  HINT,
  INPUT,
  RESULT_CARD,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { downloadText } from "@/lib/tool-kit/download";
import {
  type AcrosticLetter,
  capitalize,
  carryLines,
  fillLines,
  MAX_LETTERS,
  parseWord,
  poemText,
  printPage,
  startsWithLetter,
  suggestionsFor,
  THEME_IDS,
  type ThemeId,
} from "@/lib/tool-kit/poetry/acrostic";
import { printHtmlDocument } from "@/lib/tool-kit/print";

const CARROLL = [
  "A boat beneath a sunny sky,",
  "Lingering onward dreamily",
  "In an evening of July—",
  "Children three that nestle near,",
  "Eager eye and willing ear,",
  "Pleased a simple tale to hear—",
  "Long has paled that sunny sky:",
  "Echoes fade and memories die.",
  "Autumn frosts have slain July.",
  "Still she haunts me, phantomwise,",
  "Alice moving under skies",
  "Never seen by waking eyes.",
  "Children yet, the tale to hear,",
  "Eager eye and willing ear,",
  "Lovingly shall nestle near.",
  "In a Wonderland they lie,",
  "Dreaming as the days go by,",
  "Dreaming as the summers die:",
  "Ever drifting down the stream—",
  "Lingering in the golden gleam—",
  "Life, what is it but a dream?",
] as const;

const VISIBLE_CHIPS = 8;

/** English strings. A translated content file overrides these through `ui`. */
export const ACROSTIC_POEM_GENERATOR_STRINGS = {
  wordLabel: "Word, name or phrase to spell down the page",
  wordPlaceholder: "SPRING",
  wordHint:
    "Up to {max} letters. Spaces start a new stanza. Accents are dropped and other characters are ignored.",
  themeLabel: "Theme for the word suggestions",
  themes: {
    general: "Anything",
    nature: "Nature",
    ocean: "Ocean",
    love: "Love",
    friendship: "Friendship",
    family: "Family",
    home: "Home",
    winter: "Winter",
    spring: "Spring",
    summer: "Summer",
    autumn: "Autumn",
    school: "School and learning",
    courage: "Courage",
    celebration: "Celebration",
    night: "Night and stars",
    gratitude: "Gratitude",
  },
  titleLabel: "Title (optional, used for printing)",
  defaultTitle: "Acrostic: {word}",
  honest:
    "Honest templates, no AI: the suggestions are hand-written word lists for each theme, and every line is yours to edit.",
  suggest: "Suggest a word for each letter",
  shuffle: "Shuffle suggestions",
  clearLines: "Clear lines",
  sampleLabel: "See a famous acrostic",
  sampleWord: "ALICE PLEASANCE LIDDELL",
  sampleLines: CARROLL,
  sampleCredit:
    "Lewis Carroll, the closing poem of Through the Looking-Glass (1871): the first letters of its lines spell the name of Alice Liddell. Public domain.",
  emptyPrompt: "Type a word above to get one line for each of its letters.",
  ignored: "Ignored: {characters}",
  tooLong: "Only the first {max} letters are used.",
  linesHeading: "Your lines",
  lineLabel: "Line starting with {letter}",
  linePlaceholder: "{letter}…",
  wrongLetter: "This line does not start with {letter}.",
  chipsLabel: "Words starting with {letter}",
  showAll: "Show all {count} words",
  showFewer: "Show fewer",
  moreCount: "one:{count} word|other:{count} words",
  previewHeading: "Your acrostic",
  copyPoem: "Copy poem",
  copied: "Copied!",
  download: "Download .txt",
  print: "Print",
  filename: "acrostic.txt",
} as const;

type Strings = typeof ACROSTIC_POEM_GENERATOR_STRINGS;

const ID = "acrostic";

function randomFloat(): number {
  const buffer = new Uint32Array(1);
  crypto.getRandomValues(buffer);
  return buffer[0] / 2 ** 32;
}

function LineRow({
  index,
  entry,
  value,
  theme,
  expanded,
  onChange,
  onToggle,
  strings,
}: {
  index: number;
  entry: AcrosticLetter;
  value: string;
  theme: ThemeId;
  expanded: boolean;
  onChange: (value: string) => void;
  onToggle: () => void;
  strings: Strings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const words = suggestionsFor(theme, entry.letter);
  const shown = expanded ? words : words.slice(0, VISIBLE_CHIPS);
  const wrong =
    value.trim().length > 0 && !startsWithLetter(value, entry.letter);
  const fieldId = `${ID}-line-${index}`;
  return (
    <li className={entry.gapBefore ? "mt-6 space-y-2" : "space-y-2"}>
      <div className="flex items-center gap-3">
        <label
          className="w-9 shrink-0 text-center font-semibold text-2xl text-primary"
          htmlFor={fieldId}
        >
          {entry.letter}
          <span className="sr-only">
            {t(strings.lineLabel, { letter: entry.letter })}
          </span>
        </label>
        <input
          className={INPUT}
          id={fieldId}
          onChange={(event) => onChange(event.target.value)}
          placeholder={t(strings.linePlaceholder, { letter: entry.letter })}
          type="text"
          value={value}
        />
      </div>
      {wrong ? (
        <p className="pl-12 text-destructive text-xs" role="alert">
          {t(strings.wrongLetter, { letter: entry.letter })}
        </p>
      ) : null}
      <div
        className="flex flex-wrap items-center gap-1.5 pl-12"
        title={t(strings.chipsLabel, { letter: entry.letter })}
      >
        {shown.map((word) => (
          <button
            className={BUTTON_SMALL}
            key={word}
            onClick={() => onChange(capitalize(word))}
            type="button"
          >
            {word}
          </button>
        ))}
        {words.length > VISIBLE_CHIPS ? (
          <button
            className="text-primary text-xs underline"
            onClick={onToggle}
            type="button"
          >
            {expanded
              ? strings.showFewer
              : t(strings.showAll, {
                  count: tPlural(strings.moreCount, locale, words.length, {
                    count: fmt(words.length),
                  }),
                })}
          </button>
        ) : null}
      </div>
    </li>
  );
}

export default function AcrosticPoemGeneratorWidget() {
  const strings = useToolUi(ACROSTIC_POEM_GENERATOR_STRINGS);
  const fmt = useToolNumber();
  const [word, setWord] = useState("");
  const [theme, setTheme] = useState<ThemeId>("general");
  const [title, setTitle] = useState("");
  const [lines, setLines] = useState<string[]>([]);
  const [expanded, setExpanded] = useState<number | null>(null);
  const parsed = useMemo(() => parseWord(word), [word]);
  const letters = parsed.letters;

  function changeWord(next: string) {
    const after = parseWord(next).letters;
    setLines((previous) => carryLines(previous, letters, after));
    setWord(next);
    setExpanded(null);
  }

  function setLine(index: number, value: string) {
    setLines((previous) =>
      letters.map((_, i) => (i === index ? value : (previous[i] ?? "")))
    );
  }

  function loadSample() {
    const sample = parseWord(strings.sampleWord).letters;
    setWord(strings.sampleWord);
    setLines(sample.map((_, i) => strings.sampleLines[i] ?? ""));
    setExpanded(null);
  }

  const heading =
    title.trim() || t(strings.defaultTitle, { word: word.trim() });
  const text = poemText(letters, lines);
  const hasLines = lines.some((line) => line.trim().length > 0);
  const ignored = parsed.ignored.join(" ");

  return (
    <>
      <TextField
        hint={t(strings.wordHint, { max: fmt(MAX_LETTERS) })}
        id={`${ID}-word`}
        label={strings.wordLabel}
        onChange={changeWord}
        placeholder={strings.wordPlaceholder}
        value={word}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id={`${ID}-theme`}
          label={strings.themeLabel}
          onChange={setTheme}
          options={THEME_IDS.map((id) => ({
            value: id,
            label: strings.themes[id],
          }))}
          value={theme}
        />
        <TextField
          id={`${ID}-title`}
          label={strings.titleLabel}
          onChange={setTitle}
          value={title}
        />
      </div>
      <p className={HINT}>{strings.honest}</p>
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_PRIMARY}
          disabled={letters.length === 0}
          onClick={() => setLines(fillLines(letters, theme, randomFloat))}
          type="button"
        >
          {hasLines ? strings.shuffle : strings.suggest}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={!hasLines}
          onClick={() => setLines(letters.map(() => ""))}
          type="button"
        >
          {strings.clearLines}
        </button>
        <button className={BUTTON_SECONDARY} onClick={loadSample} type="button">
          {strings.sampleLabel}
        </button>
      </div>
      {parsed.ignored.length > 0 ? (
        <Notice>{t(strings.ignored, { characters: ignored })}</Notice>
      ) : null}
      {parsed.tooLong ? (
        <Notice>{t(strings.tooLong, { max: fmt(MAX_LETTERS) })}</Notice>
      ) : null}

      {letters.length > 0 ? (
        <section className="space-y-5">
          <h2 className={WIDGET_H2}>{strings.linesHeading}</h2>
          <ol className="space-y-3">
            {letters.map((entry, index) => (
              <LineRow
                entry={entry}
                expanded={expanded === index}
                index={index}
                // biome-ignore lint/suspicious/noArrayIndexKey: one row per position of the word
                key={index}
                onChange={(value) => setLine(index, value)}
                onToggle={() => setExpanded(expanded === index ? null : index)}
                strings={strings}
                theme={theme}
                value={lines[index] ?? ""}
              />
            ))}
          </ol>
          <div aria-live="polite" className={RESULT_CARD}>
            <p className="mb-3 text-muted-foreground text-xs">
              {strings.previewHeading}
            </p>
            <p className="mb-3 text-center text-foreground tracking-wide">
              {heading}
            </p>
            <div className="font-serif text-foreground text-lg leading-relaxed">
              {letters.map((entry, i) => {
                const line = (lines[i] ?? "").trim() || entry.letter;
                return (
                  <p
                    className={entry.gapBefore ? "mt-4" : undefined}
                    // biome-ignore lint/suspicious/noArrayIndexKey: one row per position of the word
                    key={i}
                  >
                    <b className="text-xl">{line.charAt(0)}</b>
                    {line.slice(1)}
                  </p>
                );
              })}
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copyPoem}
              text={text}
            />
            <button
              className={BUTTON_SECONDARY}
              onClick={() => downloadText(strings.filename, `${text}\n`)}
              type="button"
            >
              {strings.download}
            </button>
            <button
              className={BUTTON_SECONDARY}
              onClick={() =>
                printHtmlDocument(printPage(heading, letters, lines))
              }
              type="button"
            >
              {strings.print}
            </button>
          </div>
          {lines.join("") === strings.sampleLines.join("") ? (
            <p className={HINT}>{strings.sampleCredit}</p>
          ) : null}
        </section>
      ) : (
        <Notice>{strings.emptyPrompt}</Notice>
      )}
    </>
  );
}
